import classnames from "classnames";
import { floor, split } from "lodash";
import { ArrowUpDown, RefreshCcw, SortAsc, SortDesc } from "lucide-react";
import { motion } from "motion/react";
import { Fragment, useState } from "react";
import { useParams } from "react-router-dom";

import Preloader from "@krea/customer-features/shared-components/preloader";
import { Button } from "@krea/shared/design-system/Button";
import { Label } from "@krea/shared/design-system/Label";
import { ResponsiveTooltip } from "@krea/shared/design-system/ResponsiveTooltip";
import { Spinner } from "@krea/shared/design-system/Spinner";
import { cn } from "@krea/shared/utils";

import { LenderApplicationsFilterEnum } from "@/features/common/enums";
import { useGetLenderApplicationListQuery } from "@/features/lender-applications/hooks/data/use-get-lender-application-list.query";
import settings from "@/settings";
import { getTitleAndDescriptionForFilters } from "@/utils";
import { useMobile } from "@/utils/customHooks/useMobile";

import { Item } from "../item/index";

const SortCell = ({
  title,
  currentSort,
  sort,
  defaultDirection,
  setSorting,
  nowrap,
}: {
  title: string;
  currentSort: string;
  sort: string;
  defaultDirection: string;
  setSorting: (sort: string) => void;
  nowrap?: boolean;
}) => {
  const parsedSort = split(currentSort, ":");
  const currentSortBy = parsedSort[0];
  const currentDirection = parsedSort[1];

  const onSetSort = () => {
    let direction;

    if (sort === currentSortBy) {
      direction = currentDirection === "asc" ? "desc" : "asc";
    } else {
      direction = defaultDirection;
    }

    setSorting(`${sort}:${direction}`);
  };

  return (
    <div
      className={classnames("flex cursor-pointer items-center", {
        active: currentSortBy === sort,
      })}
      onClick={onSetSort}
    >
      <div
        className={classnames("mr-2", { "whitespace-nowrap": nowrap })}
      >
        {title}
      </div>
      {currentSortBy === sort &&
        (currentDirection === "asc" ? (
          <SortAsc className="size-4" />
        ) : (
          <SortDesc className="size-4" />
        ))}
      {currentSortBy !== sort && <ArrowUpDown className="size-4" />}
    </div>
  );
};

const EmptyListMessage = ({
  className,
}: {
  className?: string;
  show?: boolean;
}) => {
  return <div className={classnames("flex", className)}>No items</div>;
};

export const List = ({
  className,
  filter,
}: {
  className?: string;
  filter: LenderApplicationsFilterEnum;
}) => {
  const isMobile = useMobile();
  const [currentSort, setSorting] = useState(
    settings.defaultListSort[filter as keyof typeof settings.defaultListSort],
  );

  const { lenderCompanyId } = useParams();
  const { data, isFetching, isPending, refetch, fetchNextPage, hasNextPage } =
    useGetLenderApplicationListQuery(Number(lenderCompanyId), {
      filter,
      sort: currentSort,
    });
  const lenderApplications = data?.pages.flatMap((page) => page.items);
  const count = data?.pages[0]?.count ?? 0;

  const { title, description } = getTitleAndDescriptionForFilters(
    filter,
    count,
  );

  const emptyListMessage = !isFetching && !count;

  const loadMore = () => {
    fetchNextPage();
  };

  return (
    <motion.section
      initial="collapsed"
      animate="open"
      exit="collapsed"
      variants={{
        open: {
          opacity: 1,
          height: "auto",
          transition: {
            type: "tween",
            duration: 0.2,
            ease: "easeOut",
          },
        },
        collapsed: {
          opacity: 0,
          height: 0,
          transition: {
            type: "tween",
            duration: 0.0,
            ease: "easeOut",
          },
        },
      }}
      transition={{ duration: 0.2 }}
      className={classnames("mb-9", className)}
    >
      <div className="mb-5">
        <div className="flex justify-between">
          <div className="w-full">
            {title && <h1 className="m-0 text-lg">{title}</h1>}
            {description && (
              <div
                className={classnames({
                  "mt-2": title,
                })}
              >
                {description}
              </div>
            )}
          </div>
          <div className="shrink-0 pl-4">
            <ResponsiveTooltip
              trigger={
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => refetch()}
                  disabled={isFetching}
                  className="mr-2"
                >
                  {isFetching ? (
                    <Spinner className="size-4" />
                  ) : (
                    <RefreshCcw className="size-4" />
                  )}
                </Button>
              }
              interactive
            >
              Update this list
            </ResponsiveTooltip>
          </div>
        </div>
      </div>
      <div
        className={classnames("relative", {
          "shadow-sm": !isMobile,
        })}
      >
        {!isMobile ? (
          <Fragment>
            {isPending ? (
              <Preloader className="text-primary" size="lg" />
            ) : null}

            {emptyListMessage && (
              <EmptyListMessage
                className="absolute flex h-full w-full items-center justify-center"
                show={emptyListMessage}
              />
            )}

            <div className="min-h-[130px] w-full overflow-x-auto">
              {count > 0 && (
                <>
                  <table
                    className={classnames(
                      "w-full min-w-[1110px] text-left text-sm [&_td]:px-2 [&_td]:py-2 [&_th]:px-2 [&_th]:py-2",
                      className,
                    )}
                  >
                    <thead>
                      <tr>
                        <th>
                          <SortCell
                            currentSort={currentSort}
                            setSorting={setSorting}
                            sort="id"
                            defaultDirection="asc"
                            title="#"
                          />
                        </th>
                        <th className="whitespace-nowrap">Product</th>

                        {![
                          LenderApplicationsFilterEnum.PAID_OUT,

                          LenderApplicationsFilterEnum.BID_SELECTED,
                        ].includes(filter) && (
                          <th>
                            <SortCell
                              currentSort={currentSort}
                              setSorting={setSorting}
                              sort="marked_eligible_for_lender_at"
                              defaultDirection="desc"
                              title="Received"
                              nowrap
                            />
                          </th>
                        )}

                        {[
                          LenderApplicationsFilterEnum.NO_COMPANY_LOAN_BIDS,
                          LenderApplicationsFilterEnum.NO_INVOICE_PURCHASE_BIDS,
                          LenderApplicationsFilterEnum.NO_REAL_ESTATE_BIDS,
                        ].includes(filter) && (
                          <th>
                            <SortCell
                              currentSort={currentSort}
                              setSorting={setSorting}
                              sort="loan_application__bidding_ends_at"
                              defaultDirection="desc"
                              title="Time left"
                              nowrap
                            />
                          </th>
                        )}

                        {filter === LenderApplicationsFilterEnum.PAID_OUT && (
                          <th>
                            <SortCell
                              currentSort={currentSort}
                              setSorting={setSorting}
                              sort="loan_application__payout_marked_as_received_at"
                              defaultDirection="desc"
                              title="Paid out at"
                              nowrap
                            />
                          </th>
                        )}

                        {[LenderApplicationsFilterEnum.BID_SELECTED].includes(
                          filter,
                        ) && (
                          <th>
                            <SortCell
                              currentSort={currentSort}
                              setSorting={setSorting}
                              sort="loan_application__bid_selected_at"
                              defaultDirection="desc"
                              title="Bid accepted at"
                              nowrap
                            />
                          </th>
                        )}

                        <th className="whitespace-nowrap">
                          <SortCell
                            currentSort={currentSort}
                            setSorting={setSorting}
                            sort="loan_application__requested_loan_amount"
                            defaultDirection="asc"
                            title={
                              filter !==
                              LenderApplicationsFilterEnum.NO_LEASING_BIDS
                                ? "Amount"
                                : "Leasing amount"
                            }
                          />
                        </th>

                        <th className="text-center">
                          <SortCell
                            currentSort={currentSort}
                            setSorting={setSorting}
                            sort="loan_application__requested_payback_time_months"
                            defaultDirection="asc"
                            title="Months"
                          />
                        </th>
                        <th>
                          <SortCell
                            currentSort={currentSort}
                            setSorting={setSorting}
                            sort="loan_application__applying_company__customer_company__name"
                            defaultDirection="asc"
                            title="Company"
                          />
                        </th>
                        <th>
                          <SortCell
                            currentSort={currentSort}
                            setSorting={setSorting}
                            sort="loan_application__applying_company__customer_company__type"
                            defaultDirection="asc"
                            title="Type"
                          />
                        </th>
                        <th className="whitespace-nowrap">Org. number</th>
                        <th className="whitespace-nowrap">Credit Score</th>
                        <th className="whitespace-nowrap">
                          <SortCell
                            currentSort={currentSort}
                            setSorting={setSorting}
                            sort="estimated_revenue_last_12_months" // this is a manually "computed" on the backend
                            defaultDirection="desc"
                            title="Est. revenue last 12 months"
                            nowrap
                          />
                        </th>
                        <th className="whitespace-nowrap">
                          <div className="rotate">Guarantor</div>
                        </th>
                        {/*
                        <th style={{ textAlign: "center" }}>
                          <div className="rotate">Financial information</div>
                        </th>
                        <th style={{ textAlign: "center" }}>
                          <div className="rotate">Securities</div>
                        </th>
                        <th style={{ textAlign: "center" }}>
                          <div className="rotate">Other info</div>
                        </th> */}
                        <th className="whitespace-nowrap">Assigned to</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {lenderApplications?.map((lenderApplication, index) => (
                        <Item
                          animationIndex={
                            index -
                            floor(index / settings.defaultListLimit) *
                              settings.defaultListLimit
                          }
                          key={`${filter}-${lenderApplication.id}`}
                          mobile={isMobile}
                          lenderApplication={lenderApplication}
                          filter={filter}
                          rowIndex={index}
                        />
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </Fragment>
        ) : (
          <Fragment>
            <div className="space-y-2">
              <Label htmlFor="sort-by">Sort by</Label>
              <select
                id="sort-by"
                value={currentSort}
                onChange={(e) => setSorting(e.target.value)}
                className={cn(
                  "h-10 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm",
                  "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                )}
              >
                <option value="id:asc">ID (ascending)</option>
                <option value="id:desc">ID (descending)</option>
                {![
                  LenderApplicationsFilterEnum.PAID_OUT,
                  LenderApplicationsFilterEnum.BID_SELECTED,
                ].includes(filter) && (
                  <Fragment>
                    <option value="loan_application__created_at:asc">
                      Application received time (ascending)
                    </option>
                    <option value="loan_application__created_at:desc">
                      Application received time (descending)
                    </option>
                  </Fragment>
                )}
                {[
                  LenderApplicationsFilterEnum.NO_COMPANY_LOAN_BIDS,
                  LenderApplicationsFilterEnum.NO_INVOICE_PURCHASE_BIDS,
                  LenderApplicationsFilterEnum.NO_REAL_ESTATE_BIDS,
                ].includes(filter) && (
                  <Fragment>
                    <option value="loan_application__bidding_ends_at:asc">
                      Bidding ending time (ascending)
                    </option>
                    <option value="loan_application__bidding_ends_at:desc">
                      Bidding ending time (descending)
                    </option>
                  </Fragment>
                )}
                {filter === LenderApplicationsFilterEnum.PAID_OUT && (
                  <Fragment>
                    <option value="loan_application__payout_marked_as_received_at:asc">
                      Paid out time (ascending)
                    </option>
                    <option value="loan_application__payout_marked_as_received_at:desc">
                      Paid out time (descending)
                    </option>
                  </Fragment>
                )}
                {[LenderApplicationsFilterEnum.BID_SELECTED].includes(
                  filter,
                ) && (
                  <Fragment>
                    <option value="loan_application__bid_selected_at:asc">
                      Bid selection time (ascending)
                    </option>
                    <option value="loan_application__bid_selected_at:desc">
                      Bid selection time (descending)
                    </option>
                  </Fragment>
                )}
                <option value="loan_application__requested_loan_amount:asc">
                  Requested amount (ascending)
                </option>
                <option value="loan_application__requested_loan_amount:desc">
                  Requested amount (descending)
                </option>
                <option value="loan_application__requested_payback_time_months:asc">
                  Requested repayment time (ascending)
                </option>
                <option value="loan_application__requested_payback_time_months:desc">
                  Requested repayment time (descending)
                </option>
                <option value="estimated_revenue_last_12_months:asc">
                  Est. revenue last 12 months (ascending)
                </option>
                <option value="estimated_revenue_last_12_months:desc">
                  Est. revenue last 12 months (descending)
                </option>
              </select>
            </div>
            {lenderApplications?.map((lenderApplication, index) => (
              <Item
                animationIndex={
                  index -
                  floor(index / settings.defaultListLimit) *
                    settings.defaultListLimit
                }
                key={lenderApplication.id}
                mobile={isMobile}
                lenderApplication={lenderApplication}
                filter={filter}
              />
            ))}
            {emptyListMessage && <EmptyListMessage className="" />}
            {isPending ? (
              <Preloader className="text-primary" size="lg" />
            ) : null}
          </Fragment>
        )}
      </div>
      <div className="mb-9 mt-4 flex flex-col items-center justify-center xl:mt-5 xl:flex-row xl:items-center xl:justify-start">
        {hasNextPage ? (
          <Button
            className="order-2 mt-3 px-4 xl:order-1 xl:mt-0"
            onClick={loadMore}
          >
            Show more
          </Button>
        ) : null}
        {lenderApplications && lenderApplications.length > 0 && count > 0 && (
          <div className="order-1 xl:order-2 xl:ml-3">{`${lenderApplications.length} of ${count}`}</div>
        )}
      </div>
    </motion.section>
  );
};
